import { OpusAgentAttachmentInput } from "@src/types.generated";
import type {
  OpusAgentAttachmentLibraryItemFragment,
  OpusAgentAttachmentMediaFragment,
  OpusAgentAttachmentStatusFieldsFragment,
} from "./useOpusAgentAttachments.generated";

// A composer attachment's life: the file is uploading to S3 (library items
// skip this), the server is reading it, it is ready for the agent, or it
// failed (upload, media creation or reading) and must be removed to send.
export type OpusAgentAttachmentPillState =
  | "uploading"
  | "processing"
  | "ready"
  | "failed";

export interface OpusAgentAttachmentPill {
  key: string;
  state: OpusAgentAttachmentPillState;
  // Server- or client-side note shown on hover (a failure reason, progress).
  detail?: string | null;
  // The upload's file name, shown until its Media exists.
  fileName?: string;
  // The picked file, kept so a failed upload can be retried. A file refused
  // before upload (type, size) has none: retrying cannot help it.
  file?: File;
  media?: OpusAgentAttachmentMediaFragment | null;
  libraryItem?: OpusAgentAttachmentLibraryItemFragment | null;
  // startOpusAgentAttachments has answered, so the status poll may ask.
  started?: boolean;
  // While uploading: how much of the file has reached S3, 0–100.
  uploadPercent?: number | null;
  // The server's latest answer: which step it is on, and once read, what
  // it found (pages, text in a picture).
  status?: OpusAgentAttachmentStatusFieldsFragment | null;
}

// What a sent message carries: the pill's server object, in pill order.
export interface OpusAgentSendAttachment {
  media?: OpusAgentAttachmentMediaFragment | null;
  libraryItem?: OpusAgentAttachmentLibraryItemFragment | null;
}

export type OpusAgentSendGate =
  | { blocked: false; reason: null }
  | { blocked: true; reason: string };

export const SEND_GATE_READING = "Wait for your files to finish reading";
export const SEND_GATE_FAILED = "Remove the file that couldn't be read to send";

// Send waits for every pill to be ready. A failed pill never becomes ready,
// so it names the fix (remove it) rather than the wait.
export const attachmentSendGate = (
  pills: readonly Pick<OpusAgentAttachmentPill, "state">[],
): OpusAgentSendGate => {
  if (pills.some((pill) => pill.state === "failed"))
    return { blocked: true, reason: SEND_GATE_FAILED };
  if (
    pills.some(
      (pill) => pill.state === "uploading" || pill.state === "processing",
    )
  )
    return { blocked: true, reason: SEND_GATE_READING };
  return { blocked: false, reason: null };
};

// A failed pill can be tried again once it has something to redo: the
// server object to read, or the picked file to upload.
export const canRetryAttachment = (
  pill: Pick<
    OpusAgentAttachmentPill,
    "state" | "file" | "media" | "libraryItem"
  >,
): boolean =>
  pill.state === "failed" && !!(pill.media || pill.libraryItem || pill.file);

export const attachmentInput = (
  attachment: OpusAgentSendAttachment,
): OpusAgentAttachmentInput | null => {
  if (attachment.media) return { mediaId: attachment.media.id };
  if (attachment.libraryItem)
    return { libraryItemId: attachment.libraryItem.id };
  return null;
};

export const attachmentInputs = (
  attachments: readonly OpusAgentSendAttachment[],
): OpusAgentAttachmentInput[] =>
  attachments
    .map(attachmentInput)
    .filter((input): input is OpusAgentAttachmentInput => input !== null);
